import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, Upload, Building2, Clock, Ban } from "lucide-react";
import { SmsConsent } from "../components/legal/SmsConsent";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * 公开分包商资料页只提供中文和英文。优先读取浏览器首选语言（移动浏览器通常跟随手机系统设置），
 * 让表单整页使用同一种语言；首选语言不是中文时回退英文，避免再次出现逐项中英并排。
 */
const getSubOnboardLanguage = () => {
  const browser = typeof navigator === "undefined" ? {} : navigator;
  const preferred = (Array.isArray(browser.languages) && browser.languages[0]) || browser.language || "";
  return /^zh(?:[-_]|$)/i.test(preferred.trim()) ? "zh" : "en";
};

const FIELDS = [
  ["contact", { zh: "联系人", en: "Contact Name" }, "text"],
  ["phone", { zh: "电话", en: "Phone" }, "text"],
  ["email", { zh: "电子邮箱", en: "Email" }, "text"],
  ["license", { zh: "承包商执照号", en: "Contractor License Number" }, "text"],
  ["taxId", { zh: "税号（EIN/SSN）", en: "Tax ID (EIN/SSN)" }, "text"],
  ["address", { zh: "邮寄地址", en: "Mailing Address" }, "text"],
  ["workersCompNo", { zh: "工伤保险单号", en: "Workers' Comp Insurance No." }, "text"],
  ["workersCompExp", { zh: "工伤保险到期日", en: "Workers' Comp Expiration Date" }, "date"],
];

export default function SubOnboard() {
  const { token } = useParams();
  const [lang] = useState(getSubOnboardLanguage);
  const zh = lang === "zh";
  const t = (chinese, english) => zh ? chinese : english;
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [upBusy, setUpBusy] = useState("");
  const [uploaded, setUploaded] = useState({});
  const w9Ref = useRef(null);
  const wcRef = useRef(null);

  useEffect(() => {
    fetch(BACKEND + "/api/public/sub-onboard/" + token)
      .then((r) => { if (!r.ok) { setErr(r.status === 410 ? "expired" : "invalid"); return null; } return r.json(); })
      .then((d) => { if (!d) return; setData(d); setForm(Object.fromEntries(FIELDS.map(([k]) => [k, d[k] || ""]))); })
      .catch(() => setErr("invalid"));
  }, [token]);

  const upload = async (kind, file) => {
    if (!file) return;
    setUpBusy(kind);
    try {
      const fd = new FormData(); fd.append("kind", kind); fd.append("file", file);
      const r = await fetch(BACKEND + "/api/public/sub-onboard/" + token + "/upload", { method: "POST", body: fd });
      if (!r.ok) throw new Error();
      setUploaded((u) => ({ ...u, [kind]: file.name }));
    } catch { alert(t("上传失败，请重试", "Upload failed. Please try again.")); }
    setUpBusy("");
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await fetch(BACKEND + "/api/public/sub-onboard/" + token, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error();
      setDone(true);
    } catch { alert(t("保存失败，请重试", "Save failed. Please try again.")); }
    setBusy(false);
  };

  if (err === "expired") return (
    <div lang={zh ? "zh-CN" : "en"} className="min-h-screen flex flex-col items-center justify-center gap-2 text-slate-400 text-sm px-6 text-center" data-testid="sub-onboard-expired">
      <Ban className="h-8 w-8 text-slate-300" />
      <p className="font-semibold text-slate-500">{t("链接已失效或已关闭", "This link has expired or been closed")}</p>
      <p className="text-xs">{t("如需更新资料，请联系 ALIGCUS 获取新链接", "Please contact ALIGCUS for a new link")}</p>
    </div>
  );
  if (err) return <div lang={zh ? "zh-CN" : "en"} className="min-h-screen flex items-center justify-center text-slate-400 text-sm" data-testid="sub-onboard-invalid">{t("链接无效或已失效", "Invalid or expired link")}</div>;
  if (!data) return <div lang={zh ? "zh-CN" : "en"} className="min-h-screen flex items-center justify-center" aria-label={t("正在加载", "Loading")}><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;

  const UploadRow = ({ kind, label, hasFile, inputRef }) => (
    <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
      <div>
        <p className="text-sm font-medium text-slate-700">{label}</p>
        {(uploaded[kind] || hasFile) && <p className="text-[11px] text-emerald-600">{uploaded[kind] ? t("已上传：" + uploaded[kind], "Uploaded: " + uploaded[kind]) : t("已有存档，可重新上传", "Already on file. You may upload a replacement.")}</p>}
      </div>
      <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden" data-testid={"sub-onboard-file-" + kind} onChange={(e) => upload(kind, (e.target.files || [])[0])} />
      <button type="button" data-testid={"sub-onboard-upload-" + kind} disabled={upBusy === kind} onClick={() => inputRef.current?.click()}
        className="inline-flex items-center gap-1.5 text-xs text-white bg-slate-800 hover:bg-slate-900 rounded-md px-3 py-2 disabled:opacity-50">
        {upBusy === kind ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}{t("上传", "Upload")}
      </button>
    </div>
  );

  return (
    <div lang={zh ? "zh-CN" : "en"} className="min-h-screen bg-slate-100 py-8 px-4" data-testid="sub-onboard-page">
      <div className="max-w-xl mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-slate-900 px-6 py-5">
          <div className="flex items-center gap-2 text-white">
            <Building2 className="h-5 w-5 text-red-400" />
            <span className="font-bold">{zh ? "阿里建筑" : "ALIGCUS Construction"}</span>
          </div>
          <p className="text-slate-300 text-xs mt-1">{t("分包商资料在线更新", "Vendor File Update")}</p>
        </div>
        {done ? (
          <div className="p-10 text-center" data-testid="sub-onboard-done">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
            <p className="text-lg font-semibold text-slate-800">{t("感谢您！资料已提交", "Thank you! Submission received")}</p>
            <p className="text-sm text-slate-400 mt-1">{t("资料已提交，ALIGCUS 财务审核确认后即正式归档。", "Your submission is under review. Our finance team will verify and archive your information.")}</p>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div>
              <p className="text-sm font-semibold text-slate-800" data-testid="sub-onboard-company">{data.name}{data.legalName && data.legalName !== data.name ? " · " + data.legalName : ""}</p>
              <p className="text-[11px] text-slate-400">{t("请核对并补齐以下资料", "Please review and complete the information below")}</p>
            </div>
            {data.pendingReview && (
              <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="sub-onboard-pending-note">
                <Clock className="h-3.5 w-3.5 shrink-0" />
                <span>{t("您此前提交的资料正在审核中，仍可修改后重新提交。", "Your previous submission is under review. You may still update and resubmit.")}</span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {FIELDS.map(([k, label, type]) => (
                <label key={k} className={"block " + (k === "address" ? "sm:col-span-2" : "")}>
                  <span className="text-xs text-slate-500">{label[lang]}</span>
                  <input type={type} lang={zh ? "zh-CN" : "en"} data-testid={"sub-onboard-field-" + k} value={form[k] || ""}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="mt-1 w-full rounded-md border border-slate-200 px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300" />
                </label>
              ))}
            </div>
            <div className="space-y-2">
              <UploadRow kind="w9" label={t("W-9 表", "W-9 Form")} hasFile={data.w9OnFile} inputRef={w9Ref} />
              <UploadRow kind="wc" label={t("工伤保险证明", "Workers' Comp Certificate")} hasFile={data.hasWcFile} inputRef={wcRef} />
            </div>
            <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2"><SmsConsent value={form.smsConsent} onChange={(v) => setForm({ ...form, smsConsent: v })} testid="sub-onboard-sms-consent" compact language={lang} /></div>
            <button type="button" data-testid="sub-onboard-submit" disabled={busy} onClick={submit}
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-lg py-3 text-sm disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{t("提交资料", "Submit")}
            </button>
            <p className="text-[10px] text-slate-300 text-center">{t("本安全链接由 ALIGCUS 提供。提交内容经团队审核后归档，仅用于分包商合规管理（1099 / 保险）。", "Secure link provided by ALIGCUS. Submissions are reviewed by our team before archiving. Information is used for vendor compliance (1099 / insurance) only.")}</p>
          </div>
        )}
      </div>
    </div>
  );
}
